import { useEffect, useState, type FC } from 'react'
import { useHealth } from '@/hooks/useHealth';
import { Link } from 'react-router-dom';

type Props = { className?: string };

export const HealthBanner: FC<Props> = ({ className }) => {
  const { data, loading, lastFetchedAt, refresh } = useHealth();
  const [muted, setMuted] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem('healthBannerMuted') === '1'
    } catch {
      return false
    }
  })

  const unhealthy = !loading && (
    !data.gateway.ok || !data.users.ok || !data.users.db_ok || !data.lineage.ok
  );

  // Auto-reset mute when system is healthy again
  useEffect(() => {
    if (!unhealthy && muted) {
      setMuted(false)
      try { sessionStorage.removeItem('healthBannerMuted') } catch {}
    }
  }, [unhealthy, muted])

  if (!unhealthy || muted) return null;

  const down: string[] = [];
  if (!data.gateway.ok) down.push('Gateway');
  if (!data.users.ok) down.push('Usuarios');
  if (!data.users.db_ok) down.push('Base de datos');
  if (!data.lineage.ok) down.push('Lineage');

  const message = down.length
    ? `Problemas detectados: ${down.join(', ')}.`
    : 'Algunos servicios no están disponibles o la base de datos no responde.';

  function formatRelative(ts: number) {
    const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (sec < 60) return `${sec} s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} h`;
    const d = Math.floor(hr / 24);
    return `${d} d`;
  }

  return (
    <div
      className={`w-full bg-amber-100 text-amber-900 border border-amber-300 px-3 py-2 text-sm flex items-center justify-between ${
        className ?? ''
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        <span className="inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" aria-hidden />
        <span>{message}</span>
        {lastFetchedAt && (
          <span className="text-xs text-muted-foreground ml-2">
            Último chequeo: hace {formatRelative(lastFetchedAt)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={refresh}
          className="underline decoration-1 underline-offset-2 hover:opacity-80"
        >
          Reintentar
        </button>
        <Link
          to="/diagnostics"
          className="underline decoration-1 underline-offset-2 hover:opacity-80"
        >
          Diagnóstico
        </Link>
        <button
          onClick={() => {
            setMuted(true)
            try { sessionStorage.setItem('healthBannerMuted', '1') } catch {}
          }}
          className="text-xs text-muted-foreground hover:opacity-80"
        >
          Ocultar por sesión
        </button>
      </div>
    </div>
  );
};

export default HealthBanner;
